import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ContentCard from "../../../components/misc/ContentCard";
import { TextField } from "@mui/material";
import ModalWrapper from "../../../components/Modal/ModalWrapper";
import LoadingIndicator from "../../../components/misc/LoadingIndicator";
import CustomButton from "../../../components/CustomButton";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";
import {
  addWorkingPointTms,
  getNewErpWorkingPoints,
} from "../../../redux/api/expedition.api";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { ErpWorkingPointInterface } from "../../../redux/interfaces/expedition.interface";
import {
  clearErpWorkingPointResults,
  resetErpWorkingPoints,
} from "../../../redux/reducer/expedition.reducer";
import Text from "../../../styleguide/Text";
import { useDebounce } from "../../../hooks/useDebounce";
import SvgIcon from "../../../helpers/SvgIcon";

const detailFields: {
  key: keyof ErpWorkingPointInterface;
  label: string;
}[] = [
  { key: "id_pctLucruErp", label: "ID punct de lucru ERP" },
  { key: "pl_id", label: "ID punct de lucru" },
  { key: "ruta_id", label: "ID ruta" },
  { key: "denumire", label: "Denumire" },
  { key: "adresa", label: "Adresa" },
  { key: "localitate", label: "Localitate" },
  { key: "judet", label: "Judet" },
  { key: "cod_postal", label: "Cod postal" },
  { key: "pers_contact", label: "Persoana de contact" },
  { key: "tel_contact", label: "Telefon de contact" },
  { key: "email", label: "Email" },
  { key: "status", label: "Status" },
];

export default function AddWorkingPointTms() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { erpWorkingPoints } = useAppSelector((state) => state.expedition);
  const [selectedWorkingPoint, setSelectedWorkingPoint] =
    useState<ErpWorkingPointInterface | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);

  useEffect(() => {
    return () => {
      dispatch(resetErpWorkingPoints());
    };
  }, [dispatch]);

  useEffect(() => {
    const search = String(debouncedSearchValue).trim();
    if (
      showSearch &&
      search &&
      search === searchValue.trim() &&
      searchValue.length >= 3
    ) {
      const request = dispatch(getNewErpWorkingPoints(search));
      return () => {
        request.abort();
      };
    }
    dispatch(clearErpWorkingPointResults());
  }, [debouncedSearchValue, searchValue, showSearch, dispatch]);

  const handleSave = async () => {
    if (!selectedWorkingPoint || isLoading) return;
    setIsLoading(true);
    const resultAction = await dispatch(
      addWorkingPointTms(selectedWorkingPoint),
    );
    if (
      checkEndpointStatus(resultAction, addWorkingPointTms) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }
    if (
      checkEndpointStatus(resultAction, addWorkingPointTms) ===
      endpointStatus.fulfilled
    ) {
      setSelectedWorkingPoint(null);
      setSearchValue("");
      dispatch(clearErpWorkingPointResults());
    }
  };

  const handleOnCardClick = (point: ErpWorkingPointInterface) => {
    setSelectedWorkingPoint(point);
    setShowSearch(false);
  };

  const formatValue = (
    key: keyof ErpWorkingPointInterface,
    value: ErpWorkingPointInterface[keyof ErpWorkingPointInterface],
  ) => {
    return value === null || value === "" ? "-" : String(value);
  };

  return (
    <ContentCard cardTitle="Adauga punct de lucru in TMS">
      <CustomButton
        variant="outlined"
        className="w-full p-5"
        sx={{
          minHeight: 140,
          flexDirection: "column",
          gap: 2,
          border: "1px solid",
          borderColor: "primary.light",
          borderRadius: 2,
          backgroundColor: "rgba(33, 150, 243, 0.04)",
          textTransform: "none",
          color: "#000",
          "&:hover": {
            border: "2px solid",
            borderColor: "primary.main",
            backgroundColor: "rgba(33, 150, 243, 0.08)",
          },
          "&.Mui-focusVisible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 3,
          },
        }}
        disabled={isLoading}
        onClick={() => {
          setSearchValue("");
          setShowSearch(true);
        }}>
        <span className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-brand/10 text-black">
          <SvgIcon type="WORKING_POINT" fill="currentColor" />
        </span>
        <div className="flex flex-col items-center text-center">
          <p className="text-base font-semibold mb-0">
            {selectedWorkingPoint?.denumire ||
              "Adauga un punct de lucru in TMS"}
          </p>
          <p className="text-base mt-2 text-brand mb-0">
            {selectedWorkingPoint
              ? "Schimba punctul de lucru"
              : "Cauta si alege punctul de lucru"}
          </p>
        </div>
      </CustomButton>
      <ModalWrapper
        title="Alege punctul de lucru"
        modalSize="md"
        show={showSearch}
        handleClose={() => setShowSearch(false)}>
        <TextField
          fullWidth
          autoFocus
          label="Cauta punctul de lucru"
          placeholder="Scrie denumirea, de exemplu BI"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
        />
        <div
          className="mt-5"
          style={{ maxHeight: "50vh", overflowY: "auto" }}
          aria-live="polite">
          {erpWorkingPoints.isLoading ||
          searchValue.trim() !== String(debouncedSearchValue).trim() ? (
            <LoadingIndicator />
          ) : (
            erpWorkingPoints.data.map((point) => {
              const isDisabled = point.status === 1;
              return (
                <ContentCard
                  key={point.id_pctLucruErp}
                  className={`relative mx-0 mb-4 ${
                    isDisabled
                      ? "opacity-75 border border-danger"
                      : "cursor-pointer"
                  }`}
                  cardBodyClassName="w-full text-left bg-transparent p-0"
                  disabled={isDisabled}
                  onClick={() => handleOnCardClick(point)}>
                  <p className="font-semibold mb-2">{point.denumire}</p>
                  <p className="mb-1">{point.adresa || "-"}</p>
                  <p className="mb-1">
                    {[point.localitate, point.judet, point.cod_postal]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p className="mb-1">
                    {[point.pers_contact, point.tel_contact, point.email]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {isDisabled && (
                    <span className="inline-flex items-center rounded-control px-3 py-1 text-xs font-semibold bg-danger text-white absolute top-0 right-0 mt-3 mr-3">
                      Deja salvat in TMS
                    </span>
                  )}
                </ContentCard>
              );
            })
          )}
        </div>
      </ModalWrapper>

      {selectedWorkingPoint && (
        <div className="mt-5">
          <Text variant="h3" component="h3" className="mb-4">
            Detalii punct de lucru
          </Text>
          <div className="grid grid-cols-12 gap-4">
            {detailFields.map(({ key, label }) => (
              <div className="col-span-12 md:col-span-6 mb-4" key={key}>
                <Text variant="body1" isBold className="mb-1">
                  {label}
                </Text>
                <div className="w-full rounded-control bg-surface-subtle px-4 py-3 outline-none focus:ring-2 focus:ring-accent/30 bg-surface-subtle">
                  {formatValue(key, selectedWorkingPoint[key])}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between mt-4">
        <CustomButton variant="outlined" onClick={() => navigate(-1)}>
          Inapoi
        </CustomButton>
        <CustomButton
          variant="contained"
          loading={isLoading}
          disabled={!selectedWorkingPoint || isLoading}
          onClick={handleSave}>
          Salveaza
        </CustomButton>
      </div>
    </ContentCard>
  );
}
